import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'lodash-es';

import BlurCircularIcon from '@mui/icons-material/BlurCircular';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import HandymanIcon from '@mui/icons-material/Handyman';
import PersonIcon from '@mui/icons-material/Person';
import {
  togglePinnedFiltersState,
  selectArnmRepairsPinnedFilters,
} from '@store';
import { ARNM_REASONS_REPAIRS } from 'components/arnmRepairs/constants';
import { VIEW_MODES } from 'components/arnmRepairs/ArnmRepaisContent/ArnmRepaisContent';

export const useArnmRepairsFilterConfig = ({
  servicePoints,
  assignees,
  stages,
  viewMode,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectArnmRepairsPinnedFilters);

  const filterCategories = useMemo(
    () => [
      {
        id: 'reason',
        label: 'Reason',
        icon: ContactSupportIcon,
        options: ARNM_REASONS_REPAIRS,
        multiSelect: true,
      },
      ...(viewMode === VIEW_MODES.list.value
        ? [
            {
              id: 'stage',
              label: 'Stage',
              icon: BlurCircularIcon,
              options: stages || [],
              multiSelect: true,
            },
          ]
        : []),
      {
        id: 'servicePoint',
        label: 'Service Point',
        icon: HandymanIcon,
        options: servicePoints || [],
        multiSelect: true,
      },
      {
        id: 'assignee',
        label: 'Assignee',
        icon: PersonIcon,
        options: assignees || [],
        multiSelect: false,
      },
    ],
    [servicePoints, assignees, stages, viewMode]
  );
  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'arnmRepairs', pinnedFilter }));
  };

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      stage: parseQueryValue(router.query.stage),
      servicePoint: parseQueryValue(router.query.servicePoint),
      reason: parseQueryValue(router.query.reason),
      assignee: parseQueryValue(router.query.assignee),
    };
  }, [router.query]);

  const hasFilters =
    pinnedFilters?.length > 0 ||
    Object.keys(
      omit(router.query, [
        'page',
        'view',
        'pageSize',
        'order',
        'orderBy',
        'query',
      ])
    ).length > 0;

  return {
    hasFilters,
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
  };
};
